import React, { useState, useEffect } from 'react';
import { FaUsers, FaPlus, FaEdit, FaTrash, FaSearch, FaFilter, FaSave, FaTimes, FaEye, FaEyeSlash, FaCheck, FaUserShield, FaUserGraduate, FaUser, FaBuilding } from 'react-icons/fa';
import { databaseService, Teacher, OrganizationData } from '../../services/databaseService';

interface RealUserManagementPanelProps {
  onUserCreated?: (user: Teacher) => void;
  onUserUpdated?: (user: Teacher) => void;
  onUserDeleted?: (userId: string) => void;
}

interface UserFormData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'teacher' | 'viewer';
  password: string;
  courses: string[];
  phone?: string;
  mustChangePassword: boolean;
}

// Interfaz extendida para usuarios con nombre de organización
interface UserWithOrg extends Teacher {
  organizationName?: string;
  tenantKey?: string;
}

export const RealUserManagementPanel: React.FC<RealUserManagementPanelProps> = ({
  onUserCreated,
  onUserUpdated,
  onUserDeleted
}) => {
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all'); // Por defecto "todas"
  const [users, setUsers] = useState<UserWithOrg[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'teacher' | 'viewer'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Teacher | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'teacher',
    password: '',
    courses: [],
    phone: '',
    mustChangePassword: true
  });

  // Estado de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(25);

  useEffect(() => {
    loadOrganizations();
    // Cargar todos los usuarios al inicio
    loadAllUsers();
  }, []);

  useEffect(() => {
    if (selectedOrgId === 'all') {
      loadAllUsers();
    } else if (selectedOrgId) {
      loadUsers();
    }
  }, [selectedOrgId]);

  // Cargar TODOS los usuarios del sistema
  const loadAllUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('👥 Cargando TODOS los usuarios del sistema...');
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/system/all-users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const mappedUsers: UserWithOrg[] = data.users.map((user: any) => ({
          _id: user._id,
          username: user.username || user.email,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          role: user.role,
          organizationId: user.tenantKey,
          tenantKey: user.tenantKey,
          organizationName: user.organizationName || user.tenantKey,
          courses: user.allowedCourses || [],
          phone: user.phone || '',
          isActive: user.isActive !== undefined ? user.isActive : true,
          mustChangePassword: user.mustChangePassword !== undefined ? user.mustChangePassword : false,
          password: '***',
          lastLogin: user.lastLogin || '',
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || new Date().toISOString()
        }));
        setUsers(mappedUsers);
        console.log(`✅ Total usuarios cargados: ${mappedUsers.length}`);
      } else {
        console.warn('⚠️ Error cargando todos los usuarios:', response.statusText);
        setError('Error al cargar usuarios del sistema');
      }
    } catch (error) {
      console.error('❌ Error al cargar todos los usuarios:', error);
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const orgs = await databaseService.getOrganizations();
      setOrganizations(orgs);
      
      if (orgs.length > 0 && !selectedOrgId) {
        // Preferir "Colegio Puerto Varas" si existe, sino la primera
        const cpvOrg = orgs.find(org => org.tenantKey === 'cpv_cl' || org.displayName.includes('Puerto Varas'));
        const selectedOrg = cpvOrg || orgs[0];
        setSelectedOrgId(selectedOrg._id);
        console.log(`🎯 Organización seleccionada por defecto: ${selectedOrg.displayName} (${selectedOrg.tenantKey})`);
      }
      
      console.log(`🏢 Organizaciones cargadas: ${orgs.length}`, orgs.map(o => o.displayName));
    } catch (error) {
      console.error('Error al cargar organizaciones:', error);
      setError('Error al cargar organizaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!selectedOrgId) return;
    
    try {
      setLoading(true);
      setError(null);
      console.log(`👥 Cargando usuarios para organización: ${selectedOrgId}`);
      
      const teachers = await databaseService.getTeachers(selectedOrgId);
      setUsers(teachers);
      
      console.log(`✅ Usuarios cargados: ${teachers.length}`, teachers.map(t => `${t.firstName} ${t.lastName} (${t.email})`));
    } catch (error) {
      console.error('❌ Error al cargar usuarios:', error);
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Validaciones
      if (!formData.username || !formData.email || !formData.firstName || !formData.lastName) {
        setError('Todos los campos son obligatorios');
        return;
      }

      if (!formData.password || formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      // Verificar email único
      if (users.some(user => user.email === formData.email)) {
        setError('Ya existe un usuario con este email');
        return;
      }

      // Crear nuevo usuario
      const newUser = await databaseService.createTeacher(selectedOrgId, {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        courses: formData.courses,
        phone: formData.phone,
        isActive: true,
        mustChangePassword: formData.mustChangePassword,
        password: formData.password
      });

      setUsers(prev => [...prev, newUser]);
      setSuccess('Usuario creado exitosamente');
      setShowCreateModal(false);
      resetForm();
      
      if (onUserCreated) {
        onUserCreated(newUser);
      }

    } catch (error) {
      console.error('Error creating user:', error);
      setError('Error al crear usuario');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      setError(null);
      setSuccess(null);

      // Validaciones
      if (!formData.username || !formData.email || !formData.firstName || !formData.lastName) {
        setError('Todos los campos son obligatorios');
        return;
      }

      // Verificar email único (excepto el usuario actual)
      if (users.some(user => user.email === formData.email && user._id !== editingUser._id)) {
        setError('Ya existe un usuario con este email');
        return;
      }

      // Actualizar usuario
      const updateData: Partial<Teacher> = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        courses: formData.courses,
        phone: formData.phone,
        mustChangePassword: formData.mustChangePassword
      };

      // Solo actualizar contraseña si se proporcionó una nueva
      if (formData.password) {
        updateData.password = formData.password;
      }

      const updatedUser = await databaseService.updateTeacher(selectedOrgId, editingUser._id, updateData);
      
      if (updatedUser) {
        setUsers(prev => prev.map(user => 
          user._id === editingUser._id ? updatedUser : user
        ));
        
        setSuccess('Usuario actualizado exitosamente');
        setEditingUser(null);
        resetForm();
        
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      }

    } catch (error) {
      console.error('Error updating user:', error);
      setError('Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }

    try {
      const success = await databaseService.deleteTeacher(selectedOrgId, userId);
      
      if (success) {
        setUsers(prev => prev.filter(user => user._id !== userId));
        setSuccess('Usuario eliminado exitosamente');
        
        if (onUserDeleted) {
          onUserDeleted(userId);
        }
      }

    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Error al eliminar usuario');
    }
  };

  const handleEditUser = (user: Teacher) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      password: '',
      courses: user.courses || [],
      phone: user.phone || '',
      mustChangePassword: user.mustChangePassword
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      role: 'teacher',
      password: '',
      courses: [],
      phone: '',
      mustChangePassword: true
    });
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  // Calcular paginación
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, selectedOrgId]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <FaUserShield className="w-4 h-4" />;
      case 'teacher': return <FaUserGraduate className="w-4 h-4" />;
      default: return <FaUser className="w-4 h-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'teacher': return 'Profesor';
      case 'viewer': return 'Visualizador';
      default: return role;
    }
  };

  const selectedOrg = organizations.find(org => org._id === selectedOrgId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-3 rounded-full">
              <FaUsers className="text-blue-600 w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Gestión de Usuarios</h3>
              <p className="text-sm text-gray-600">
                Administra los usuarios del sistema ({filteredUsers.length} usuarios)
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            disabled={!selectedOrgId}
            className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <FaPlus className="w-4 h-4" />
            <span>Nuevo Usuario</span>
          </button>
        </div>
      </div>

      {/* Organization Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <FaBuilding className="text-gray-400 w-5 h-5" />
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Organización
            </label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">🌐 Todas las organizaciones</option>
              {organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.displayName} ({org.tenantKey})
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-600">
            {selectedOrgId === 'all' ? (
              <div className="flex items-center space-x-2">
                <FaUsers className="text-blue-500" />
                <span>Total usuarios del sistema: <strong>{users.length}</strong></span>
              </div>
            ) : selectedOrg && (
              <>
                <div>Usuarios: {selectedOrg.userCount}</div>
                <div>Estudiantes: {selectedOrg.studentCount}</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-green-800">{success}</div>
        </div>
      )}

      {selectedOrgId && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar usuarios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <FaFilter className="text-gray-400 w-4 h-4" />
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value as any)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Todos los roles</option>
                  <option value="admin">Administradores</option>
                  <option value="teacher">Profesores</option>
                  <option value="viewer">Visualizadores</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                {selectedOrgId === 'all' 
                  ? `Todos los usuarios del sistema (${filteredUsers.length})`
                  : `Usuarios de ${selectedOrg?.displayName} (${filteredUsers.length})`
                }
              </h4>

              {/* Selector de usuarios por página */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Mostrar:</span>
                  <select
                    value={usersPerPage}
                    onChange={(e) => {
                      setUsersPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={filteredUsers.length}>Todos ({filteredUsers.length})</option>
                  </select>
                  <span className="text-sm text-gray-600">usuarios por página</span>
                </div>
                <div className="text-sm text-gray-600">
                  Mostrando {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} de {filteredUsers.length}
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : paginatedUsers.length > 0 ? (
                <div className="space-y-4">
                  {paginatedUsers.map((user: UserWithOrg) => (
                    <div key={user._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-full ${
                          user.role === 'admin' ? 'bg-purple-100' :
                          user.role === 'teacher' ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <div className={`${
                            user.role === 'admin' ? 'text-purple-600' :
                            user.role === 'teacher' ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {getRoleIcon(user.role)}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                          <div className="text-xs text-gray-500">@{user.username}</div>
                          {/* Mostrar organización cuando se ven todos los usuarios */}
                          {selectedOrgId === 'all' && user.organizationName && (
                            <div className="text-xs text-indigo-600 mt-1 flex items-center">
                              <FaBuilding className="mr-1" />
                              {user.organizationName}
                            </div>
                          )}
                          {user.courses && user.courses.length > 0 && (
                            <div className="text-xs text-blue-600 mt-1">
                              Cursos: {user.courses.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'teacher' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getRoleLabel(user.role)}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {user.lastLogin ? 
                              `Último acceso: ${new Date(user.lastLogin).toLocaleDateString()}` :
                              'Nunca ha ingresado'
                            }
                          </div>
                          {user.mustChangePassword && (
                            <div className="text-xs text-orange-600 mt-1">
                              Debe cambiar contraseña
                            </div>
                          )}
                          {!user.isActive && (
                            <div className="text-xs text-red-600 mt-1">
                              Usuario inactivo
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Editar usuario"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Eliminar usuario"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No se encontraron usuarios</p>
                </div>
              )}

              {/* Controles de paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Página {currentPage} de {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Primera
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    
                    {/* Números de página */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm border rounded ${
                              currentPage === pageNum 
                                ? 'bg-blue-500 text-white border-blue-500' 
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Última
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Create/Edit User Modal */}
      {(showCreateModal || editingUser) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellido *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Apellido"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de Usuario *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="usuario123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`usuario@${selectedOrg?.domains[0] || 'ejemplo.cl'}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rol *
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="teacher">Profesor</option>
                      <option value="admin">Administrador</option>
                      <option value="viewer">Visualizador</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+56 9 1234 5678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña {editingUser ? '(dejar vacío para no cambiar)' : '*'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Contraseña"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-blue-500 hover:text-blue-700 text-sm mt-1"
                  >
                    Generar contraseña segura
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cursos Asignados
                  </label>
                  <input
                    type="text"
                    value={formData.courses.join(', ')}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      courses: e.target.value.split(',').map(c => c.trim()).filter(c => c) 
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1° Básico, 2° Básico (separar por comas)"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="mustChangePassword"
                    checked={formData.mustChangePassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, mustChangePassword: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="mustChangePassword" className="ml-2 text-sm text-gray-700">
                    Debe cambiar contraseña en el próximo login
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingUser ? handleUpdateUser : handleCreateUser}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <FaSave className="w-4 h-4" />
                  <span>{editingUser ? 'Actualizar' : 'Crear'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealUserManagementPanel;